import { formatLocalDate } from './utils/localDateTime';
import { isManagementPortal, managementOrigin, marketingOrigin } from './common/siteEnvironment';
import { countryRiskScore } from './utils/scoreLabel';
import { publishedEvent } from './utils/riskEventFeed';
import React, { useState, useEffect, useCallback } from 'react';
import { isOrganizationAccess, isClientView, fullWorkspaceAccess, clientWorkspaceStorage } from './common/organizationAccessMode';
import { clearClientWorkspaceData } from './common/clientWorkspaceData';
import { GlobalAssetProvider } from './context/GlobalAssetContext';
import { organizationGlobalAssets } from './common/organizationGlobalAssets';
import useGoogleMaps from './components/useGoogleMaps';
import MarketingLanding from './components/MarketingLanding';
import UpgradeBenefitsPage from './components/UpgradeBenefitsPage';
import Header from './components/Header';
import Footer from './components/Footer';
import KnowledgeHub from './components/KnowledgeHub';
import IndustrySelection from './components/IndustrySelection';
import CustomIndustryBuilder from './components/CustomIndustryBuilder';
import { Dashboard } from './components/Dashboard';
import SuiteDashboard from './components/SuiteDashboard';
// GSOCDashboard removed — functionality migrated to RiskMonitorDashboard (ECC)
import { PillarsDashboard } from './components/PillarsDashboard';
import OrganizationDashboard from './components/OrganizationDashboard';
import CountryDashboard from './components/CountryDashboard';
import { ResiliencyHub } from './components/ResiliencyHub';
import { normalizeISO3 } from './utils/geography';
import { COUNTRY_CODES } from './common/data';
import { getOrganizationDisplayName } from '@common/organizationDisplayName';
import { isPublicPathPage, parseHashRoute, routeFromLocation, serializeHashRoute } from './routing/hashRouter';
import { canUseLms, isDirectClientRouteAllowed } from './common/clientAccessPolicy';
import { canManageClientAccount } from './common/clientAccountAccess';
import { showAppAlert } from './common/appDialog';

import GlobalThreatGroupDashboard from './components/GlobalThreatGroupDashboard';
import { AnalysisResultDisplay } from './components/AnalysisResultDisplay';
import GovernanceStructureDisplay from './components/reports/GovernanceStructureDisplay';
import { ExecutiveReportViewer } from './components/ExecutiveReportViewer';
// ✅ ADDED: Your new components from the workflow update
import CarverThreatAssessmentForm from './components/CarverThreatAssessmentForm'; 
import GlobalMasterRiskRegister from './components/GlobalMasterRiskRegister';

import Contact from './components/Contact'; 
import TrainingPage from './components/TrainingPage';
import LegalPage from './components/LegalPage';
import InstructionManual from './components/InstructionManual';
import AudioVisualToolsModal from './components/AudioVisualToolsModal';
import Notepad from './components/Notepad';
import FloatingNavIndicator from './components/FloatingNavIndicator';
import AssetManagerModal from './components/AssetManagerModal';
import SrmCommandCenter from './components/SrmCommandCenter';
import CountryIntelPage from './components/CountryIntelPage';
// ✅ Agency Workspace
import AgencyDashboard from './components/AgencyDashboard';
import ClientDetailPage from './components/ClientDetailPage';

import Auth from './components/Auth';
import ClientGateway from './components/ClientGateway';
import ManagementGateway from './components/ManagementGateway';
import PendingClearancePage from './components/PendingClearancePage';
import SubscriptionSetup from './components/SubscriptionSetup';
import InactiveAccountView from './components/InactiveAccountView';
import SubscriptionManagement from './components/SubscriptionManagement';
import MsaAccessGate from './components/MsaAccessGate';
import FreeTrialNotice from './components/FreeTrialNotice';
import { hasMsaAccess } from './common/msaAccess';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import IntelligenceLibraryPage from './components/IntelligenceLibraryPage';
import ServiceRequestsPage from './components/ServiceRequestsPage';
import ConsultantReportWorkspace from './components/ConsultantReportWorkspace';
import { ConsultantReportView } from './components/ConsultantReportViewer';
import { OrganizationReportContext, ReportNavigationContext } from './common/reportNavigation';
import MyAccountPage from './components/MyAccountPage';
import BillingInvoicesPage from './components/BillingInvoicesPage';
import HelpGuidancePage from './components/HelpGuidancePage';
import ServiceRequestNotifications from './components/ServiceRequestNotifications';
import ClientServiceRequestNotifications from './components/ClientServiceRequestNotifications';
import ClientChatNotifications from './components/support/ClientChatNotifications';
import HelpdeskModal from './components/HelpdeskModal';
import GlossaryDrawer from './components/GlossaryDrawer';
import MethodologyLibrary from './components/MethodologyLibrary';
// TEMP — Remove after Aug 22, 2026
import DevImplementationDashboard from './components/DevImplementationDashboard';
import { ShieldCheckIcon, CheckCircleIcon, BookOpenIcon } from '@heroicons/react/24/outline';

// 1. Import your INITIALIZED instances from your local config file
import { db, auth, functions } from '../firebaseConfig'; 

// 2. Import FIRESTORE methods from the official library
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
    collection, onSnapshot, addDoc, doc, getDoc, setDoc, 
    updateDoc, where, query, getDocs, arrayUnion,
    orderBy, limit, serverTimestamp
} from 'firebase/firestore';

// 3. Import AUTH methods and types from the official library
import { 
    onAuthStateChanged, 
    signOut, 
    type User 
} from 'firebase/auth';

// 4. Keep your custom types
import type { 
    Page, NavParams, CustomIndustry, Student, LegalDoc, 
    UserProfile, Organization, SrmProject, ActiveFramework, 
    AcapsCrisis, SubscriptionTier, CountryRiskData, GeopoliticalEvent,
    AgencyClient, EnrolledCourse
} from '@types';



export interface HelpRequest {
    suiteName: string;
    workflowName?: string;
}
const App: React.FC = () => {
    const reportAccess = React.useContext(OrganizationReportContext);
    const reportOnly = reportAccess?.limited === true;
    const openReportOverlay = React.useContext(ReportNavigationContext);
    // --- Domain Context ---
    // Determines which portal the user is accessing to drive post-login routing.
    const IS_MANAGEMENT_PORTAL = isManagementPortal;

    // --- State Management ---
    const initialRouteRef = React.useRef(routeFromLocation(window.location));
    const [authInitialized, setAuthInitialized] = useState(false);
    const [authUser, setAuthUser] = useState<User | null>(null); // Basic user from Firebase Auth
    const [currentUser, setCurrentUser] = useState<UserProfile | null | undefined>(undefined);
    const [currentOrganization, setCurrentOrganization] = useState<Organization | null | undefined>(undefined);
    const [clientAccessDenied, setClientAccessDenied] = useState(false);
    const [, setMsaClockTick] = useState(0);
    const hasActiveMsa = hasMsaAccess(currentOrganization);
    const msaAccessBlocked = !fullWorkspaceAccess && Boolean(currentUser && currentOrganization && !hasActiveMsa);
    const msaMissing = Boolean(currentOrganization && !currentOrganization.hasMsaSubscription && !currentOrganization.msaApplicationId && !currentOrganization.msaDocument);
    useEffect(() => {
        if (currentOrganization?.msaTrialMode !== 'app' || !currentOrganization.trialEndsAt)
            return;
        const delay = Math.max(0, new Date(currentOrganization.trialEndsAt).getTime() - Date.now());
        const timer = window.setTimeout(() => setMsaClockTick(value => value + 1), Math.min(delay + 1000, 2147483647));
        return () => window.clearTimeout(timer);
    }, [currentOrganization?.msaTrialMode, currentOrganization?.trialEndsAt]);
    const isManagementAdmin = ['Super', 'Global', 'Regional'].includes(String(currentUser?.adminRole));
    const [currentPage, setCurrentPage] = useState<Page>(initialRouteRef.current.page);
    const [pendingPlan, setPendingPlan] = useState<SubscriptionTier | null>(() => {
        const saved = clientWorkspaceStorage().getItem('inteloptics_pending_plan');
        return (saved === 'individual' || saved === 'business' || saved === 'agency') ? saved as SubscriptionTier : null;
    });
    const logOut = async () => {
        if (!auth)
            return;
        try {
            await signOut(auth);
        } finally {
            clearClientWorkspaceData();
            setPendingPlan(null);
        }
    };
    
    // ... other states
    const [riskData, setRiskData] = useState<any[]>([]);
    const [orgUsers, setOrgUsers] = useState<UserProfile[]>([]);
    const [navParams, setNavParams] = useState<NavParams>(initialRouteRef.current.params);
    const [currentIndustry, setCurrentIndustry] = useState<string | null>(null);
    const [customIndustries, setCustomIndustries] = useState<CustomIndustry[]>([]);
    const students = React.useMemo<Student[]>(() => orgUsers
        .filter(user => user.role === 'learner' && user.status !== 'deleted' && !user.isDeleted)
        .map(user => {
            const names = String(user.displayName || user.email || 'Learner').trim().split(/\s+/);
            return {
                id: user.email.toLowerCase(),
                learnerId: user.uid,
                accessCode: '',
                firstName: names[0] || 'Learner',
                lastName: names.slice(1).join(' '),
                organization: currentOrganization?.name || 'Organization',
                organizationId: user.organizationId,
                email: user.email,
                subscriptionStatus: user.status || 'active',
                enrolledCourses: user.enrolledCourses || [],
            };
        }), [orgUsers, currentOrganization?.name]);
    const saveLearnerCourses = useCallback(async (studentId: string, enrolledCourses: EnrolledCourse[]) => {
        const learner = students.find(student => student.id === studentId);
        if (!learner?.email)
            throw new Error('Learner account not found.');
        const updateLearner = httpsCallable(functions, 'inviteTeamMember');
        await updateLearner({
            action: 'updateLmsLearner',
            payload: { email: learner.email, enrolledCourses },
        });
    }, [students]);
    const enrollLearner = useCallback(async (course: EnrolledCourse, studentId: string) => {
        const learner = students.find(student => student.id === studentId);
        if (!learner)
            throw new Error('Learner account not found.');
        const enrolledCourses = [...learner.enrolledCourses.filter(existing => existing.courseId !== course.courseId), course];
        await saveLearnerCourses(studentId, enrolledCourses);
    }, [students, saveLearnerCourses]);
    const updateLearnerCourse = useCallback(async (studentId: string, courseId: string, updates: Partial<EnrolledCourse>) => {
        const learner = students.find(student => student.id === studentId);
        if (!learner)
            throw new Error('Learner account not found.');
        await saveLearnerCourses(studentId, learner.enrolledCourses.map(course =>
            course.courseId === courseId ? { ...course, ...updates } : course));
    }, [students, saveLearnerCourses]);
    const [currentSrmProject, setCurrentSrmProject] = useState<SrmProject | null>(null);
    const [selectedCountryIso3, setSelectedCountryIso3] = useState<string | null>(null);
    // ✅ Agency: Active client context
    const [activeClient, setActiveClient] = useState<AgencyClient | null>(null);
    const [previousPage, setPreviousPage] = useState<Page>('organizationDashboard');
    const [previousNavParams, setPreviousNavParams] = useState<NavParams>({});
    const routeCacheRef = React.useRef(new Map<string, NavParams>());
    const restoredLocationRef = React.useRef('');

    const writeHashRoute = useCallback((page: Page, params: NavParams = {}, replace = false) => {
        if (isPublicPathPage(page))
            return;
        const hash = serializeHashRoute(page, params);
        const routeKey = crypto.randomUUID();
        routeCacheRef.current.set(routeKey, params);
        const state = { ...window.history.state, intelopticsRouteKey: routeKey };
        const url = `${window.location.pathname}${window.location.search}${hash}`;
        if (replace)
            window.history.replaceState(state, '', url);
        else
            window.history.pushState(state, '', url);
        restoredLocationRef.current = `${window.location.pathname}${window.location.hash}`;
    }, []);


    // ✅ REFACTORED: Guarded navigation function to prevent redundant state updates
    const transitionToPage = useCallback((targetPage: Page) => {
        if (currentPage !== targetPage) {
            console.log(`🧭 Navigation transition: ${currentPage} -> ${targetPage}`);
            setCurrentPage(targetPage);
            setNavParams({});
            writeHashRoute(targetPage, {}, true);
        }
    }, [currentPage, writeHashRoute]);

    useEffect(() => {
        const restoreBrowserRoute = () => {
            const locationKey = `${window.location.pathname}${window.location.hash}`;
            if (restoredLocationRef.current === locationKey)
                return;
            restoredLocationRef.current = locationKey;
            const route = routeFromLocation(window.location);
            const routeKey = window.history.state?.intelopticsRouteKey;
            const cachedParams = typeof routeKey === 'string' ? routeCacheRef.current.get(routeKey) : undefined;
            setPreviousPage(currentPage);
            setPreviousNavParams(navParams);
            setCurrentPage(route.page);
            setNavParams(cachedParams ? { ...route.params, ...cachedParams } : route.params);
            window.scrollTo(0, 0);
        };
        window.addEventListener('popstate', restoreBrowserRoute);
        window.addEventListener('hashchange', restoreBrowserRoute);
        return () => {
            window.removeEventListener('popstate', restoreBrowserRoute);
            window.removeEventListener('hashchange', restoreBrowserRoute);
        };
    }, [currentPage, navParams]);

    React.useLayoutEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, [currentPage]);

    useEffect(() => {
        if (!authInitialized || !authUser || isPublicPathPage(currentPage))
            return;
        const parsed = parseHashRoute(window.location.hash);
        if (!parsed || parsed.page !== currentPage)
            writeHashRoute(currentPage, navParams, true);
    }, [authInitialized, authUser, currentPage, navParams, writeHashRoute]);
    // TEMP — Remove after Aug 22, 2026 (used by DevImplementationDashboard sprint button)
    React.useEffect(() => {
        (window as any).__inteloptics_navigate = ({ page }: { page: Page }) => {
            setCurrentPage(page);
            setNavParams({});
            writeHashRoute(page, {});
        };
        return () => { delete (window as any).__inteloptics_navigate; };
    }, [writeHashRoute]);

    const [activeFramework, setActiveFramework] = useState<ActiveFramework | null>(null);
    const [allActiveFrameworks, setAllActiveFrameworks] = useState<ActiveFramework[]>([]);
    const [globalAcapsData, setGlobalAcapsData] = useState<(AcapsCrisis | GeopoliticalEvent)[]>([]);
    const [acapsLastUpdated, setAcapsLastUpdated] = useState<Date | null>(null);
    const [newCrisisAlert, setNewCrisisAlert] = useState<AcapsCrisis | null>(null);
    const [helpContext, setHelpContext] = useState<HelpRequest | null>(null);
    const [isAvToolsOpen, setIsAvToolsOpen] = useState(false);
    const [isNavIndicatorVisible, setIsNavIndicatorVisible] = useState(true);
    const { isLoaded } = useGoogleMaps(); 
    const [isAssetManagerOpen, setIsAssetManagerOpen] = useState(false);
    const [isRefreshingAnalysis, setIsRefreshingAnalysis] = useState(false);
    const [isHelpdeskModalOpen, setIsHelpdeskModalOpen] = useState(false);
    const [helpdeskTicketRefresh, setHelpdeskTicketRefresh] = useState(0);
    const [helpdeskCategory, setHelpdeskCategory] = useState<'Industry Help and Support' | 'Application IT Help and Support'>('Industry Help and Support');
    const [isGlossaryOpen, setIsGlossaryOpen] = useState(false);
    const [glossarySearch, setGlossarySearch] = useState<string | null>(null);
    // ✅ Post-payment sync: tracks whether we're waiting for webhook to update org tier
    const [checkoutPending, setCheckoutPending] = useState(() => 
        window.location.search.includes('checkout=success')
    );

    const clearCustomerState = () => {
        setRiskData([]);
        setOrgUsers([]);
        setCustomIndustries([]);
        setCurrentSrmProject(null);
        setActiveClient(null);
        setActiveFramework(null);
        setAllActiveFrameworks([]);
        setHelpContext(null);
        routeCacheRef.current.clear();
    };

    const handleOpenGlossary = useCallback((term?: string) => {
        if (term) setGlossarySearch(term);
        else setGlossarySearch(null);
        setIsGlossaryOpen(true);
    }, []);

    // --- Data Creation ---
const createNewUserProfile = async (user: User): Promise<{ userProfile: UserProfile; orgData: Organization | null; clientAccessDenied: boolean; }> => {
    if (!functions) throw new Error("Firebase Functions is not initialized.");
    const provision = httpsCallable(functions, isOrganizationAccess ? 'getOrganizationAccess' : 'inviteTeamMember');
    const result = await provision({ action: 'provisionUserProfile', payload: { plan: pendingPlan } });
    const provisioned = result.data as { userProfile: UserProfile; organization: Organization | null; clientAccessDenied?: boolean };
    return { userProfile: provisioned.userProfile, orgData: provisioned.organization, clientAccessDenied: provisioned.clientAccessDenied === true };

    /* Legacy client provisioning retained temporarily for migration reference.
    if (!db) throw new Error("Firestore is not initialized.");

    const userEmail = user.email!.toLowerCase().trim();
    const userDocRef = doc(db, "users", userEmail); // Document key is ALWAYS email
    const userDocSnap = await getDoc(userDocRef);

    if (userDocSnap.exists()) {
        // --- 🟢 EXISTING OWNER / USER PATH ---
        const existingData = userDocSnap.data() as UserProfile;
        
        const userProfile = existingData;
        
        const orgId = userProfile.organizationId || "default_org"; 
        const orgSnap = await getDoc(doc(db, "organizations", orgId));
        
        return { 
            userProfile, 
            orgData: { ...(orgSnap.data() as Organization), id: orgSnap.id } 
        };

    } else {
        // --- 🔴 BRAND NEW USER PATH ---
        
        // --- STEP 1: RESTORATION CHECK (Owner Return) ---
        const orgsRef = collection(db, "organizations");
        const restoreQuery = query(orgsRef, where("ownerEmail", "==", userEmail));
        const restoreSnap = await getDocs(restoreQuery);

        if (!restoreSnap.empty) {
            const restoredOrg = restoreSnap.docs[0];
            const restoredOrgData = restoredOrg.data() as Organization;
            
            // Re-link user to their inactive org instead of creating a new one
            const newUserProfile: UserProfile = {
                uid: user.uid,
                displayName: user.displayName || userEmail.split('@')[0],
                email: userEmail,
                role: 'owner',
                organizationId: restoredOrg.id,
                status: 'active'
            };

            await setDoc(doc(db, "users", userEmail), newUserProfile);
            return {
                userProfile: newUserProfile,
                orgData: { ...restoredOrgData, id: restoredOrg.id }
            };
        }

        // --- STEP 2: DOMAIN DISCOVERY ---
        const userDomain = userEmail.split('@')[1];
        const isPublicDomain = ['gmail.com', 'outlook.com', 'yahoo.com', 'hotmail.com', 'example.com'].includes(userDomain);
        
        // 1. Search for existing organization with this domain (SKIP for individual tier)
        if (!isPublicDomain && pendingPlan !== 'individual') {
            const orgsRef = collection(db, "organizations");
            const q = query(orgsRef, where("primaryDomain", "==", userDomain));
            const orgsSnap = await getDocs(q);
            
            if (!orgsSnap.empty) {
                const matchedOrg = orgsSnap.docs[0];
                const matchedOrgData = matchedOrg.data() as Organization;
                
                // USER SYNC: Add to existing Master Org as PENDING
                const newUserProfile: UserProfile = {
                    uid: user.uid,
                    displayName: user.displayName || userEmail.split('@')[0],
                    email: userEmail,
                    role: 'viewer', // Default to viewer for new domain joins
                    organizationId: matchedOrg.id,
                    status: 'pending_approval', // GATE: Requires Admin Approval
                    createdAt: new Date().toISOString()
                };
                
                await setDoc(doc(db, "users", userEmail), newUserProfile);
                
                return {
                    userProfile: newUserProfile,
                    orgData: { ...matchedOrgData, id: matchedOrg.id }
                };
            }
        }

        // --- STEP 2: BRAND NEW OWNER (No Domain Match) ---
        const initialTier = (pendingPlan === 'individual' || pendingPlan === 'business') 
            ? pendingPlan 
            : 'individual'; 

        const isInternal = userEmail.endsWith('@eprims.com') || userEmail.endsWith('@inteloptics.org'); // @inteloptics.org kept for legacy accounts during transition
        const defaultOrgName = `${userEmail.split('@')[0]}'s Organization`;
        const orgDataObj: Omit<Organization, 'id'> = { 
            name: defaultOrgName,
            ownerId: user.uid,
            tier: isInternal ? 'business' : initialTier, 
            subscriptionStatus: isInternal ? 'active' : 'inactive',
            maxSeats: (isInternal || initialTier === 'business') ? 10 : 1,
            seatsUsed: 1,
            totalAdmins: 1,
            totalUsers: (isInternal || initialTier === 'business') ? 9 : 0,
            extraAdminSeats: 0,
            extraUserSeats: 0,
            createdAt: new Date().toISOString(),
            brandName: defaultOrgName,
            ownerEmail: userEmail, // Store for restoration lookup
            primaryDomain: isPublicDomain ? null : userDomain // Store domain for future discovery
        };

        const orgDocRef = await addDoc(collection(db, "organizations"), orgDataObj);
        
        // 2. Create the User Profile using the EMAIL as the Document ID
        const emailPrefix = userEmail.split('@')[0];
        const formattedName = emailPrefix
            .split(/[._-]/)
            .map(part => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');

        const newUserProfile: UserProfile = {
            uid: user.uid,
            displayName: formattedName, 
            email: userEmail,
            role: 'owner',
            organizationId: orgDocRef.id,
            status: 'active'
        };

        // FIX: Ensuring the new doc is indexed by Email-ID
        await setDoc(doc(db, "users", userEmail), newUserProfile);
        
        return { 
            userProfile: newUserProfile, 
            orgData: { ...orgDataObj, id: orgDocRef.id } 
        };
    } */
};

// EFFECT 1: Handle Auth State & Trigger Sync
useEffect(() => {
    if (!auth) {
        setAuthInitialized(true);
        return;
    }

    let previousUid: string | null = null;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (previousUid && previousUid !== user?.uid) {
            clearClientWorkspaceData();
            clearCustomerState();
            setPendingPlan(null);
            // Discard Firestore's in-memory documents before another login.
            window.location.reload();
            return;
        }
        previousUid = user?.uid || null;
        setAuthUser(user);
        setClientAccessDenied(false);
        
        if (user) {
            try {
                // 🟢 TRIGGER THE SYNC: This creates or updates the doc
                // and fetches the organization data.
                const { userProfile, orgData, clientAccessDenied: denied } = await createNewUserProfile(user);
                if (auth.currentUser?.uid !== user.uid)
                    return;
                
                setCurrentUser(userProfile);
                setCurrentOrganization(orgData);
                setClientAccessDenied(!IS_MANAGEMENT_PORTAL && denied);
                console.log("✅ Profile synced and loaded.");
            } catch (err) {
                if (auth.currentUser?.uid !== user.uid)
                    return;
                console.error("❌ Sync Error:", err);
                setCurrentUser(null);
                setCurrentOrganization(null);
                setClientAccessDenied(!IS_MANAGEMENT_PORTAL);
            }
        } else {
            setCurrentUser(null);
            setCurrentOrganization(null);
            const publicRoute = routeFromLocation(window.location);
            if (isPublicPathPage(publicRoute.page)) {
                setCurrentPage(publicRoute.page);
                setNavParams(publicRoute.params);
            } else {
                setCurrentPage('auth');
                setNavParams({});
                window.history.replaceState({}, '', '/auth');
            }
        }
        setAuthInitialized(true);
    });

    return () => unsubscribe();
}, [auth]);

// EFFECT 2: The Data Listener (Refined)
useEffect(() => {
    // 1. Guard: Only start if auth is fully ready and has an email
    if (!authInitialized || !authUser?.email || !db || reportOnly) return;

    const userEmail = authUser.email.toLowerCase().trim();
    const userDocRef = doc(db, "users", userEmail);
    let unsubscribeOrg: (() => void) | null = null;
    
    const unsubscribeUser = onSnapshot(userDocRef, (userDoc) => {
        if (auth.currentUser?.uid !== authUser.uid)
            return;
        if (userDoc.exists()) {
            const userData = isOrganizationAccess
                ? { ...userDoc.data(), organizationId: currentOrganization?.id, role: 'admin',
                    ...(isClientView ? { adminRole: null } : {}),
                    suitePermissions: { security_risk_management: 'admin', intel_hub: 'admin', crisis_management: 'admin', hsse: 'admin' } } as UserProfile
                : userDoc.data() as UserProfile;
            if (userData.isDeleted || ['deleted', 'suspended'].includes(String(userData.status))) {
                setCurrentUser(null);
                setCurrentOrganization(null);
                setClientAccessDenied(!IS_MANAGEMENT_PORTAL);
                return;
            }
            setCurrentUser(userData);

            if (userData.organizationId) {
                const orgDocRef = doc(db!, "organizations", userData.organizationId);
                if (unsubscribeOrg) unsubscribeOrg();

                unsubscribeOrg = onSnapshot(orgDocRef, (orgDoc) => {
                    if (auth.currentUser?.uid !== authUser.uid)
                        return;
                    if (orgDoc.exists() && orgDoc.data().isDeleted !== true) {
                        setCurrentOrganization({ 
                            ...orgDoc.data() as Organization, 
                            id: orgDoc.id 
                        });
                        setClientAccessDenied(false);
                    } else {
                        setCurrentOrganization(null);
                        setClientAccessDenied(!IS_MANAGEMENT_PORTAL);
                    }
                }, (err) => {
                    // SILENT LOG: If we already have data from createNewUserProfile, 
                    // a transient permission error on the listener shouldn't wipe the suite.
                    console.warn("Org Sync Listener (Handled):", err.code);
                    if (!currentOrganization) setCurrentOrganization(null);
                });
            } else if (!isOrganizationAccess) {
                setCurrentOrganization(null);
                setClientAccessDenied(!IS_MANAGEMENT_PORTAL);
            }
        } else {
            console.warn("No document found for:", userEmail);
            setCurrentUser(null);
            setCurrentOrganization(null);
            setClientAccessDenied(!IS_MANAGEMENT_PORTAL);
        }
    }, (error) => {
        console.warn("User Sync Listener (Handled):", error.code);
        if (!currentUser) setCurrentUser(null);
    });

    return () => {
        unsubscribeUser();
        if (unsubscribeOrg) unsubscribeOrg();
    };
}, [authUser, authInitialized, db]);

    // EFFECT 3: Sync Organization Users (The Fix for 0/11 count)
    useEffect(() => {
        // 1. UPDATED GUARD: Only run if DB, Org, AND your Profile are fully loaded
        // AND user has Admin/Owner clearance (to avoid background permission-denied spam)
        const hasClearance = canUseLms(currentUser?.role);
        
        if (!db || !currentOrganization?.id || !authUser || !currentUser || !hasClearance || reportOnly) {
            setOrgUsers([]); 
            return;
        }

        if (currentUser.role === 'learner') {
            setOrgUsers([currentUser]);
            return;
        }

        const usersQuery = query(
            collection(db, "users"), 
            where("organizationId", "==", currentOrganization.id)
        );

        // Listen for real-time updates to the organization's user list
        const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
            if (auth.currentUser?.uid !== authUser.uid)
                return;
            const usersList = snapshot.docs.map(doc => ({
                uid: doc.id,
                ...doc.data()
            })) as UserProfile[];
            
            console.log(`👥 Organization sync: ${usersList.length} users found.`);
            setOrgUsers(usersList); // This updates the 2/11 count to 1/11 instantly
        }, (error) => {
            // SILENT CATCH: This prevents the red "Uncaught Error" in your terminal
            // when a document is deleted or permissions are being synced.
            if (error.code === 'permission-denied') {
                console.warn("⏳ Firestore is syncing permissions/document changes...");
            } else {
                console.error("❌ Critical Sync Error:", error);
            }
        });

        return () => unsubscribe();
        // 3. UPDATED DEPENDENCIES: Ensuring the effect restarts when user state changes
    }, [db, currentOrganization?.id, authUser, currentUser]);

    // ✅ Post-payment auto-escape: If org goes active, clear checkoutPending immediately.
    // If webhook is slow (>45s), force-clear anyway so user isn't permanently stuck.
    useEffect(() => {
        if (!checkoutPending) return;

        // If org just became active, clear immediately
        if (hasMsaAccess(currentOrganization)) {
            setCheckoutPending(false);
            // Strip the query param from the URL without reload
            window.history.replaceState({}, '', `${window.location.pathname}${window.location.hash}`);
            return;
        }

        // Safety net: auto-clear after 45 seconds regardless of webhook status
        const timer = setTimeout(() => {
            console.warn('⏰ Checkout pending timeout — forcing dashboard load. Webhook may still be processing.');
            setCheckoutPending(false);
            window.history.replaceState({}, '', `${window.location.pathname}${window.location.hash}`);
        }, 45_000);

        return () => clearTimeout(timer);
    }, [checkoutPending, currentOrganization?.subscriptionStatus]);
    
    useEffect(() => {
        if (currentOrganization) {
            // Filter for high-impact tactical events
            // GATE: If user is pending_approval, they stay on the clearance screen
            // BYPASS for johnferg77@gmail.com for stabilization testing
            if (currentUser?.status === 'pending_approval' && authUser?.email !== 'johnferg77@gmail.com') {
                transitionToPage('pending_clearance');
                return;
            }

            const isCheckoutSuccess = window.location.search.includes('checkout=success');
            const isActive = hasMsaAccess(currentOrganization) ||
                           isManagementAdmin;
    
            if (isActive) {
                if (['auth', 'subscriptionSetup', 'pending_clearance', 'marketing'].includes(currentPage)) {
                    if (IS_MANAGEMENT_PORTAL) {
                        if (isManagementAdmin) {
                            console.log("Management portal routing administrator.");
                            transitionToPage('superAdmin');
                        } else {
                            transitionToPage('organizationDashboard');
                        }
                    } else {
                        console.log("DEBUG: Case 1 - Org is Active. Moving to organizationDashboard.");
                        transitionToPage('organizationDashboard');
                    }
                }
            } else if (isCheckoutSuccess && currentPage === 'subscriptionSetup') {
                // If we just finished checkout, don't kick them to setup if they are already on the dashboard
                // Actually, if they are on subscriptionSetup and we see success, move them to dashboard
                // Global Intelligence Indicators
                // and the RENDER logic will show the "Finalizing" loader.
                console.log("DEBUG: Checkout success detected. Moving to organizationDashboard to poll status.");
                transitionToPage('organizationDashboard');
            } else {
                // If the organization is inactive, redirect them from branding/auth to subscription setup
                if (['auth', 'branding', 'pending_clearance'].includes(currentPage)) {
                    console.log("DEBUG: Case 1 - Org is Inactive. Redirecting to subscriptionSetup.");
                    transitionToPage('subscriptionSetup');
                }
            }
        } 
        else if (authUser && currentUser) {
            if (IS_MANAGEMENT_PORTAL && isManagementAdmin) {
                transitionToPage('superAdmin');
            } else if (!currentUser.organizationId && currentPage !== 'subscriptionSetup') {
                console.log("DEBUG: Case 2 - No Org ID found. Redirecting to subscriptionSetup.");
                transitionToPage('subscriptionSetup');
            }
        }
    }, [currentOrganization, authUser, currentUser, pendingPlan, currentPage, transitionToPage, isManagementAdmin]);
    
    // Real-time Intelligence Listener (Unified risk_events collection)
    // Sources: Global Intelligence Feeds, Tactical Event Streams, Validated Incident Streams
    useEffect(() => {
        if (!db || !currentUser || reportOnly) return;

        const risksCollection = collection(db, 'risk_events');
        const qRisks = query(risksCollection, orderBy('processedAt', 'desc'), limit(150));

        const unsubscribeRisks = onSnapshot(qRisks, (snapshot) => {
            const visibleDocs = snapshot.docs.filter(doc => publishedEvent(doc.data()));

            const riskList = visibleDocs.map(doc => {
                const data = doc.data();

                // Canonical flat fields (new schema) with backward-compat fallbacks
                const rawIso3    = data.iso3 || data.geo?.country_iso3;
                const locationName = data.location_name || data.geo?.location_name || data.location?.city || '';
                const iso3 = (rawIso3 && rawIso3 !== 'UNK')
                    ? normalizeISO3(rawIso3)
                    : normalizeISO3(data.location?.country || locationName);

                // ?? preserves valid 0 coords; normalizer already rejected null-island
                const lat = data.lat ?? data.geo?.lat ?? data.location?.lat ?? null;
                const lng = data.lng ?? data.geo?.lng ?? data.location?.lng ?? null;
                const validLat = (lat !== null && lat !== undefined) ? Number(lat) : null;
                const validLng = (lng !== null && lng !== undefined) ? Number(lng) : null;

                return {
                    ...data,
                    id:        doc.id,
                    title:     data.title || data.analysis?.intel_title || 'Intelligence Alert',
                    summary:   data.summary || data.analysis?.incident_summary || '',
                    country:   iso3,
                    iso3,
                    isAlert:   true,
                    riskScore: data.riskScore ?? data.analysis?.risk_score ?? null,
                    category:  data.category || data.analysis?.category || 'General',
                    source:    data.source || 'Strategic',
                    source_label: data.source_label || data.source || 'Intelligence',
                    latitude:  validLat ?? 0,
                    longitude: validLng ?? 0,
                    location: {
                        city:    locationName || 'Global',
                        country: iso3,
                        lat:     validLat ?? 0,
                        lng:     validLng ?? 0,
                    }
                } as any;
            });

            setGlobalAcapsData(prev => {
                const combined = [...riskList];
                const seen = new Set();
                return combined.filter(item => {
                    if (seen.has(item.id)) return false;
                    seen.add(item.id);
                    return true;
                });
            });
            setAcapsLastUpdated(new Date());
        }, (error) => console.error('Risk Events Sync Error:', error));

        return () => {
            unsubscribeRisks();
        };
    }, [db, currentUser]);


    const handleSaveThreatAssessment = async (data: any) => {
        if (!db || !currentUser) return;
        try {
            console.log('Saving Threat Assessment:', data);
            await addDoc(collection(db, 'threatAssessments'), {
                ...data,
                userId: currentUser.uid,
                organizationId: currentUser.organizationId,
                createdAt: new Date().toISOString()
            });
            void showAppAlert('Assessment saved successfully!');
        } catch (error) {
            console.error("Error saving threat assessment:", error);
            void showAppAlert('Failed to save assessment.');
        }
    };

    // Effect for organization sub-collections
    useEffect(() => {
        // ADDED: currentUser check to ensure security rules have a profile to read from
        if (!db || !currentOrganization?.id || !currentUser || reportOnly) {
            setAllActiveFrameworks([]);
            setCustomIndustries([]);
            return;
        }

        // These queries match your security rules exactly
        const frameworksQuery = query(collection(db, 'active_frameworks'), where("organizationId", "==", currentOrganization.id));
        const industriesQuery = query(collection(db, 'custom_industries'), where("organizationId", "==", currentOrganization.id));
        
        const unsubFrameworks = onSnapshot(frameworksQuery, snapshot => {
            const frameworks = snapshot.docs.map(d => ({ ...d.data(), id: d.id } as ActiveFramework));
            setAllActiveFrameworks(frameworks);
            
            // Auto-initialize activeFramework if not set
            setActiveFramework(prev => {
                if (!prev && frameworks.length > 0) return frameworks[0];
                if (prev) {
                    // Update current active framework if it changed in the list
                    const updated = frameworks.find(f => f.id === prev.id);
                    return updated || (frameworks.length > 0 ? frameworks[0] : null);
                }
                return prev;
            });
        }, (error) => console.error("[Firestore Listener Error: Frameworks]", error));

        const unsubIndustries = onSnapshot(industriesQuery, snapshot => {
            setCustomIndustries(snapshot.docs.map(d => ({ ...d.data(), id: d.id } as CustomIndustry)));
        }, (error) => console.error("[Firestore Listener Error: Industries]", error));

        return () => {
            unsubFrameworks();
            unsubIndustries();
        };
        // Added currentUser to dependency array
    }, [currentOrganization?.id, currentUser, db]);

    // ✅ Agency: Inject branding CSS variables when active client changes
    useEffect(() => {
        const root = document.documentElement;
        if (activeClient) {
            // Restore active client from navParams if needed
            root.style.setProperty('--brand-primary', '#3b82f6');
            root.style.setProperty('--brand-accent', '#6366f1');
            document.title = `${activeClient.name} | IntelOptics Insights`;
        } else {
            root.style.removeProperty('--brand-primary');
            root.style.removeProperty('--brand-accent');
            document.title = 'IntelOptics Insights';
        }
    }, [activeClient]);

    // ✅ Agency: Handler to activate a client context
    const handleActivateClient = useCallback((client: AgencyClient) => {
        setActiveClient(client);
        setNavParams(prev => ({
            ...prev,
            clientId: client.id,
            clientName: client.name,
            // Canonical activeClient fields for AI context injection
            activeClientId: client.id,
            activeClientName: client.name,
            activeClientIndustry: client.industry,
            activeClientCountries: client.operatingCountries,
            purpose: 'agency',
        }));
        console.log(`🔵 Client context activated: ${client.name} (${client.id})`);

        // ✅ Show a brief toast so user knows activation worked
        const toast = document.createElement('div');
        toast.innerHTML = `
            <div style="display:flex;align-items:center;gap:12px;">
                <div style="width:36px;height:36px;background:rgba(59,130,246,0.2);border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:900;color:#60a5fa;font-size:16px;border:2px solid rgba(59,130,246,0.4);">
                    <span data-client-initial></span>
                </div>
                <div>
                    <p style="font-size:11px;font-weight:900;color:#60a5fa;text-transform:uppercase;letter-spacing:.1em;margin:0;">Client Context Active</p>
                    <p data-client-name style="font-size:14px;font-weight:700;color:#fff;margin:2px 0 0;"></p>
                </div>
                <div style="width:8px;height:8px;background:#3b82f6;border-radius:50%;animation:ping 1s infinite;margin-left:auto;"></div>
            </div>
        `;
        const initial = toast.querySelector<HTMLElement>('[data-client-initial]');
        const name = toast.querySelector<HTMLElement>('[data-client-name]');
        if (initial) initial.textContent = client.name.charAt(0).toUpperCase();
        if (name) name.textContent = client.name;
        toast.style.cssText = 'position:fixed;bottom:24px;right:24px;background:#0f172a;border:1px solid rgba(59,130,246,0.3);color:white;padding:16px 20px;border-radius:16px;z-index:9999;box-shadow:0 8px 32px rgba(0,0,0,0.4);min-width:300px;animation:slideUp .3s ease;';
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 3500);
    }, []);

    // ✅ Agency: Deactivate client context (return to org-level)
    const handleDeactivateClient = useCallback(() => {
        setActiveClient(null);
        setNavParams(prev => {
            const { clientId, clientName, activeClientId, activeClientName, activeClientIndustry, activeClientCountries, purpose, ...rest } = prev;
            return rest;
        });
    }, []);

    /** 
     * 🛠️ GLOBAL NORMALIZATION ENGINE
     * Recursively unwraps Firestore REST API style objects (mapValue, stringValue, etc.)
     * into clean JavaScript objects. This ensures that data from both Web SDK and
     * REST/Functions pipelines is visually consistent.
     */
    const recursiveFirestoreUnwrap = (val: any): any => {
        if (!val || typeof val !== 'object') return val;
        
        if ('nullValue' in val) return null;
        if ('mapValue' in val) return recursiveFirestoreUnwrap(val.mapValue.fields || {});
        if ('stringValue' in val) return val.stringValue;
        if ('doubleValue' in val) return Number(val.doubleValue);
        if ('integerValue' in val) return Number(val.integerValue);
        if ('booleanValue' in val) return val.booleanValue;
        if ('arrayValue' in val) return (val.arrayValue.values || []).map(recursiveFirestoreUnwrap);

        const result: any = {};
        for (const key in val) {
            result[key] = recursiveFirestoreUnwrap(val[key]);
        }
        return result;
    };

    useEffect(() => {
        if (!db || !currentUser || reportOnly) return;
        const q = query(collection(db, "daily_country_analysis"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const countries = snapshot.docs.map(doc => {
                const rawData = doc.data() as any;
                const iso3 = doc.id.toUpperCase();
                
                // Use the recursive unwrap to handle deep-nested REST structures automatically
                const data = recursiveFirestoreUnwrap(rawData);
                
                const finalScore = countryRiskScore(data);

                return {
                    ...data,
                    iso3: iso3,
                    id: iso3, // for Map lookup
                    riskScore: finalScore,
                    overallGeopoliticalRiskScore: finalScore
                };
            });
            setRiskData(countries);
            console.log(`🌍 IntelOptics Global Sync: ${countries.length} countries mapped.`);
        }, (err) => console.error("Map 1 Data Error:", err));
        return () => unsubscribe();
    }, [currentUser, db]);

    const handleNavigate = (nav: { page: any; params?: any }) => {
        if (reportOnly)
            nav = { page: 'consultantReport', params: { reportId: reportAccess!.reportId } };
        if (msaAccessBlocked)
            nav = { page: 'organizationDashboard' };

        if (!fullWorkspaceAccess && currentUser && !isDirectClientRouteAllowed(nav.page, nav.params, currentUser.role, currentUser.serviceRequestAccess)) {
            nav = { page: 'organizationDashboard' };
        }

        // Sync URL for Paddle compliance & professional UX
        const pageToPath: Record<string, string> = {
            'marketing': '/',
            'branding': '/',
            'contact': '/contact',
            'terms-of-service': '/terms-of-service',
            'privacy-policy': '/privacy-policy',
            'data-and-ai-policy': '/data-and-ai-policy',
            'legal-disclaimer': '/legal-disclaimer',
            'subscription-agreement': '/subscription-agreement',
            'refund-policy': '/refund-policy',
            'cancellation-policy': '/cancellation-policy',
            'methodology': '/methodology'
        };

        const path = pageToPath[nav.page];
        if (path) {
            window.history.pushState({ page: nav.page }, '', path);
        } else if (nav.page === 'auth') {
             window.history.pushState({ page: 'auth' }, '', '/auth');
        } else {
            writeHashRoute(nav.page, nav.params || {});
        }

        if (nav.page === 'branding') {
            if (authUser && auth) {
                void logOut();
                return;
            }
            setCurrentPage('branding');
            return;
        }

        // ✅ Standard Page Navigation
        // When navigating TO a page, if it's NOT the manual, we save the current state as the 'previous' state.
        // If we are currently ON the manual, we don't save it as the 'previous' state because we want 
        // to return to the page BEFORE the manual.
        if (nav.page !== currentPage && nav.page !== 'instructionManual' && currentPage !== 'instructionManual') {
            setPreviousPage(currentPage);
            setPreviousNavParams(navParams);
        }
        
        setCurrentPage(nav.page);
        const newParams = nav.params || {};
        setNavParams(newParams);
        
        // Ensure scroll to top on navigation
        window.scrollTo(0, 0);

        console.log(`🧭 Navigation transition: ${currentPage} -> ${nav.page}`, nav.params);
        
        if (newParams.industry) setCurrentIndustry(newParams.industry);
        if (newParams.srmProject) setCurrentSrmProject(newParams.srmProject);
        if (newParams.activeFramework) setActiveFramework(newParams.activeFramework);
        
        // Agency: persist clientId when navigating but only if provided
        if (newParams.clientId && newParams.clientId !== navParams.clientId) {
            setNavParams(prev => ({
                ...prev,
                ...newParams,
            }));
        }
    };

    useEffect(() => {
        if (reportOnly && (currentPage !== 'consultantReport' || navParams.reportId !== reportAccess!.reportId)) {
            setCurrentPage('consultantReport');
            setNavParams({ reportId: reportAccess!.reportId });
            writeHashRoute('consultantReport', { reportId: reportAccess!.reportId });
            return;
        }
        if (msaAccessBlocked && currentPage !== 'organizationDashboard') {
            setCurrentPage('organizationDashboard');
            setNavParams({});
            writeHashRoute('organizationDashboard', {});
            return;
        }
        if (fullWorkspaceAccess || !currentUser || isDirectClientRouteAllowed(currentPage, navParams, currentUser.role, currentUser.serviceRequestAccess))
            return;
        setCurrentPage('organizationDashboard');
        setNavParams({});
        writeHashRoute('organizationDashboard', {});
    }, [currentPage, currentUser, navParams, msaAccessBlocked, writeHashRoute, reportOnly, reportAccess?.reportId]);

    const handleRequestHelp = (suiteName: string, workflowName?: string) => {
        setHelpContext({ suiteName, workflowName });
        handleNavigate({ page: 'help' });
    };

    const handleAuthLogin = async () => {
        // App.tsx's onAuthStateChanged listener handles the actual navigation
        console.log("🔐 Login initiated...");
    };

    // 2. THE FIX: Define handleSignUp (Resolves Error 2304)
    const handleSignUp = async (email: string, password: string, name: string): Promise<void> => {
        if (!auth) return;
        try {
            // You might need to add: setIsLoading(true);
            const { createUserWithEmailAndPassword } = await import('firebase/auth');
            await createUserWithEmailAndPassword(auth, email, password);
        } catch (error: any) {
            console.error("❌ Signup Error:", error.code, error.message);
            // CRITICAL: Stop the loading state here!
            // setIsLoading(false); 
            
            // Show the actual error so you can see if it's "auth/unauthorized-domain"
            void showAppAlert(`Authentication Error: ${error.message}`);
            throw error;
        }
    };

    const handleNavigateToAuth = (plan?: SubscriptionTier) => { 
        if (plan) clientWorkspaceStorage().setItem('inteloptics_pending_plan', plan);
        else clientWorkspaceStorage().removeItem('inteloptics_pending_plan');
        
        setPendingPlan(plan || null); 
        handleNavigate({ page: 'auth' });
    };

    const handleSaveSrmProject = async (project: SrmProject) => {
        if (db && currentUser && currentOrganization) {
            try {
                // Use existing ID if available, otherwise generate one
                const projectId = project.id || `PROJ-${Date.now()}`;
                
                // Final save/update
                await setDoc(doc(db, 'srm_projects', projectId), {
                    ...project,
                    id: projectId, // Ensure ID is inside the doc
                    projectName: project.name, // Ensure backward compatibility field
                    userId: currentUser.uid,
                    organizationId: currentOrganization.id,
                    updatedAt: new Date().toISOString(),
                    // Only set createdAt if it doesn't exist
                    createdAt: project.createdAt || new Date().toISOString()
                }, { merge: true });
                
                console.log(`SRM Project ${projectId} saved successfully.`);
            } catch (error) {
                console.error("Error saving SRM project:", error);
            }
        }
    };

    const handleUpdateSrmProjectModule = async (projectId: string, moduleId: string, resultData: any) => {
        if (!db) return;
        try {
            const projectRef = doc(db, 'srm_projects', projectId);
            const projectSnap = await getDoc(projectRef);
            if (projectSnap.exists()) {
                const projectData = projectSnap.data() as SrmProject;
                const updatedModules = projectData.modules.map(m => 
                    m.id === moduleId ? { ...m, status: 'Ready' as const, resultData } : m
                );

                const completedModule = updatedModules.find(m => m.id === moduleId);
                const newAuditEntry = {
                    id: crypto.randomUUID().split('-')[0],
                    timestamp: new Date().toISOString(),
                    user: currentUser?.email || 'System',
                    action: 'Module Completed',
                    details: `Strategic component "${completedModule?.title || moduleId}" finalized and integrated into blueprint.`
                };

                const allReady = updatedModules.every(m => m.status === 'Ready' || m.status === 'Active');
                const newStatus = allReady ? 'Active' : projectData.status;

                await updateDoc(projectRef, { 
                    modules: updatedModules,
                    status: newStatus,
                    auditLog: arrayUnion(newAuditEntry)
                });
                
                // Update local state if it's the current project
                if (currentSrmProject?.id === projectId) {
                    setCurrentSrmProject({ 
                        ...projectData, 
                        modules: updatedModules, 
                        status: newStatus,
                        id: projectId,
                        auditLog: [...(projectData.auditLog || []), newAuditEntry]
                    });
                }
            }
        } catch (error) {
            console.error("Error updating SRM project module:", error);
        }
    };
    
    const handleDeployFramework = async (framework: ActiveFramework) => {
        if (db) {
            await addDoc(collection(db, 'active_frameworks'), framework);
        }
        handleNavigate({ page: 'srm_command_center', params: { projectId: framework.projectId } });
    };

    const handleSubscriptionSuccess = (org: Organization) => {
        setCurrentOrganization(org);
        handleNavigate({ page: 'organizationDashboard' });
    };

// --- RENDER LOGIC ---

const legalSource = new URLSearchParams(window.location.search).get('source');
const isEprimsLegalSource = legalSource === 'eprims';
const handleLegalBack = () => {
    if (isEprimsLegalSource) {
        window.location.assign(marketingOrigin + '/');
        return;
    }

    handleNavigate({ page: authUser ? 'organizationDashboard' : 'auth' });
};
const legalBackLabel = isEprimsLegalSource ? 'Back to Eprims' : 'Back to IntelOptics';

if (!authInitialized) {
    return <div className="h-screen flex items-center justify-center bg-slate-900 text-white">Initializing Intelligence Network...</div>;
}


// 🔵 MARKETING PAGE: New professional placeholder
if (currentPage === 'marketing') {
    return <MarketingLanding onNavigateToAuth={handleNavigateToAuth} onNavigate={(nav: any) => handleNavigate(nav)} />;
}


// 🟡 UPGRADE BENEFITS PAGE: Premium tier marketing & feature showcase
if (currentPage === 'upgradeBenefits') {
    return (
        <UpgradeBenefitsPage
            currentTier={currentOrganization?.tier}
            onBack={() => handleNavigate({ page: currentOrganization ? 'organizationDashboard' : 'dashboard' })}
            onNavigate={(nav: any) => handleNavigate(nav)}
            onUpgrade={(tier) => handleNavigate({ page: 'subscriptionSetup', params: { preselectedPlan: tier } })}
        />
    );
}

// 🔵 BRANDING PAGE: Always accessible as the landing page
if (currentPage === 'branding') {
    return <MarketingLanding onNavigateToAuth={handleNavigateToAuth} onNavigate={(nav: any) => handleNavigate(nav)} />;
}

if (!authUser) {
    // ── Detect which portal we're serving ──────────────────────────────────
    const isManagementDomain = isManagementPortal;

    if (currentPage === 'contact') {
        return (
            <Contact 
                onNavigate={handleNavigate} 
                currentUser={null}
                currentOrganization={null}
            />
        );
    }

    if (['terms-of-service', 'privacy-policy', 'data-and-ai-policy', 'legal-disclaimer', 'subscription-agreement', 'refund-policy', 'cancellation-policy'].includes(currentPage)) {
        return (
            <div className="min-h-screen bg-slate-50">
                <LegalPage 
                    doc={currentPage as LegalDoc} 
                    onNavigateHome={handleLegalBack}
                    backLabel={legalBackLabel}
                />
            </div>
        );
    }

    // management.inteloptics.app → Staff Management Portal
    if (isManagementDomain) {
        return (
            <ManagementGateway
                onLogin={async (email, password) => {
                    await handleAuthLogin();
                }}
                onSignUp={handleSignUp}
            />
        );
    }

    // inteloptics.app (and all other domains) → Client Portal Gateway
    return (
        <ClientGateway
            onLogin={async (email, password) => {
                await handleAuthLogin();
            }}
        />
    );
}

if (!IS_MANAGEMENT_PORTAL && clientAccessDenied) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6">
            <div className="w-full max-w-md rounded-2xl border border-red-200 bg-white p-8 text-center shadow-2xl">
                <ShieldCheckIcon className="mx-auto h-12 w-12 text-red-500" />
                <h1 className="mt-4 text-2xl font-bold text-slate-900">Access Denied</h1>
                <p className="mt-2 text-sm text-slate-600">This account is not linked to an active IntelOptics organization.</p>
                <p className="mt-3 text-sm text-slate-500">Platform administrators must use the management portal.</p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
                    <a href={managementOrigin} className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-700">Management Portal</a>
                    <button onClick={logOut} className="rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-slate-700">Log Out</button>
                </div>
                <a href="mailto:support@eprims.com" className="mt-5 inline-block text-sm font-semibold text-blue-700 hover:text-blue-800">Contact Support</a>
            </div>
        </div>
    );
}

const isProfileLoading = authUser && currentUser === undefined; // Check for undefined, not null
const isOrgLoading = authUser && currentUser?.organizationId && currentOrganization === undefined;
const isPendingRedirect = !IS_MANAGEMENT_PORTAL && authUser && currentUser && currentOrganization && ['auth'].includes(currentPage as string);

// Use type casting (as string) to fix the overlap error
if ((isProfileLoading || isOrgLoading || isPendingRedirect) && !['subscriptionSetup', 'branding'].includes(currentPage as string)) {
    return (
        <div className="h-screen flex flex-col items-center justify-center bg-slate-900 text-white transition-opacity duration-300">
            <div className="spinner mb-4 border-t-blue-500 border-4 rounded-full w-12 h-12 animate-spin"></div>
            <h2 className="text-xl font-semibold">Activating your Intelligence Suite...</h2>
            <p className="text-slate-400 mt-2">Verifying security credentials with the intelligence hub.</p>
        </div>
    );
}

if (IS_MANAGEMENT_PORTAL) {
    return (
        <div className="min-h-screen bg-slate-50">
            {currentUser && isManagementAdmin && <ServiceRequestNotifications currentUser={currentUser} />}
            {currentUser && isManagementAdmin ? (
                <SuperAdminDashboard
                    currentUser={currentUser}
                    onLogout={logOut}
                />
            ) : (
                <div className="min-h-screen flex items-center justify-center p-6">
                    <div className="max-w-md w-full rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
                        <ShieldCheckIcon className="mx-auto h-12 w-12 text-red-500" />
                        <h1 className="mt-4 text-2xl font-bold text-slate-900">Access Denied</h1>
                        <p className="mt-2 text-sm text-slate-500">Management access requires an administrator role.</p>
                        <button
                            onClick={logOut}
                            className="mt-6 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-slate-700"
                        >
                            Log Out
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// After this point, we can use ! to tell TS these are NOT null
if (currentPage === 'subscriptionSetup' && currentOrganization && !msaAccessBlocked) {
    return <SubscriptionSetup 
        orgId={currentOrganization!.id} 
        onSubscriptionSuccess={handleSubscriptionSuccess}
        onLogout={logOut}
        currentUser={currentUser!} 
        preselectedPlan={pendingPlan || 'individual'}
        onNavigate={(doc) => handleNavigate({ page: doc })}
    />;
}

if (currentPage === 'subscriptionManagement' && currentOrganization && currentUser && fullWorkspaceAccess) {
    return <SubscriptionManagement 
        currentUser={currentUser!}
        currentOrganization={currentOrganization!}
        onBack={() => handleNavigate({ page: 'knowledgeHub' })}
    />;
}

    if (currentPage === 'executiveReport') {
        return (
            <GlobalAssetProvider organization={currentOrganization}>
                <ExecutiveReportViewer 
                    config={navParams.config} 
                    reportData={navParams.reportData}
                    reportId={navParams.reportId}
                    onClose={() => handleNavigate({ page: 'organizationDashboard' })} 
                    currentUser={currentUser ?? null}
                    currentOrganization={currentOrganization ?? null}
                />
            </GlobalAssetProvider>
        );
    }

    // MAIN RETURN START
    return (
        <ReportNavigationContext.Provider value={reportId => msaAccessBlocked ? openReportOverlay?.(reportId)
            : handleNavigate({ page: 'deliveredReport', params: { reportId } })}>
        <GlobalAssetProvider organization={currentOrganization}>
            {!isOrganizationAccess && currentUser && currentOrganization && <ClientChatNotifications currentUser={currentUser} organization={currentOrganization} />}
            {!isOrganizationAccess && currentUser && currentOrganization && <ClientServiceRequestNotifications currentUser={currentUser} organization={currentOrganization} onOpen={page => {
                if (page === 'help')
                    setHelpdeskTicketRefresh(previous => previous + 1);
                handleNavigate({ page });
            }} />}
            <div className="flex flex-col min-h-screen bg-slate-50">
                <Header
                onNavigate={handleNavigate}
                currentUser={currentUser!}
                tier={currentOrganization?.tier || 'business'}
                onLogout={logOut}
                onNavigateHome={() => handleNavigate({ page: 'organizationDashboard' })}
                currentIndustry={currentIndustry}
                isNavIndicatorVisible={isNavIndicatorVisible}
                onToggleNavIndicator={() => setIsNavIndicatorVisible(!isNavIndicatorVisible)}
                onOpenGlossary={() => setIsGlossaryOpen(true)}
                fullWorkspaceAccess={fullWorkspaceAccess}
                navigationLocked={msaAccessBlocked}
            />
            {/* ── Agency Client-Mode Banner ──────────────────────────────────── */}
            {activeClient && (
                <div className="w-full bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-700 border-b border-blue-500/40 shadow-lg shadow-blue-900/30 z-40 relative">
                    <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-2 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                            {/* Pulsing indicator */}
                            <div className="relative shrink-0">
                                <div className="w-8 h-8 rounded-lg bg-white/15 border border-white/30 flex items-center justify-center font-black text-white text-sm">
                                    {activeClient.name.charAt(0).toUpperCase()}
                                </div>
                                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-blue-700 animate-pulse" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-[9px] font-black text-blue-200 uppercase tracking-[0.3em] leading-none mb-0.5">Active Client Context</p>
                                <p className="text-white font-black text-sm leading-tight truncate">{activeClient.name}</p>
                            </div>
                            {/* Meta pills */}
                            {activeClient.industry && (
                                <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-md bg-white/10 border border-white/20 text-[10px] font-bold text-blue-100 uppercase tracking-widest shrink-0">
                                    {activeClient.industry}
                                </span>
                            )}
                            {(activeClient.operatingCountries?.length ?? 0) > 0 && (
                                <span className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/10 border border-white/20 text-[10px] font-bold text-blue-100 shrink-0">
                                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" /></svg>
                                    {activeClient.operatingCountries!.slice(0, 2).join(', ')}{activeClient.operatingCountries!.length > 2 ? ` +${activeClient.operatingCountries!.length - 2}` : ''}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <button
                                onClick={() => handleNavigate({ page: 'clientDetail', params: { clientId: activeClient.id, clientName: activeClient.name } })}
                                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white text-[10px] font-black uppercase tracking-widest transition-all"
                            >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                View Client
                            </button>
                            <button
                                onClick={handleDeactivateClient}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-red-500/30 border border-white/20 hover:border-red-400/40 text-white hover:text-red-200 text-[10px] font-black uppercase tracking-widest transition-all"
                                title="Deactivate client context"
                            >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                                Deactivate
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div className="flex flex-1">
                {!msaAccessBlocked && isNavIndicatorVisible && (
                    <FloatingNavIndicator
                        onNavigate={handleNavigate}
                        activeSuite={navParams.suiteName}
                        currentPage={currentPage}
                        navParams={navParams}
                        purpose={navParams.purpose}
                        clientName={navParams.clientName}
                        tier={currentOrganization?.tier}
                        onOpenAssetManager={() => setIsAssetManagerOpen(true)}
                        fullWorkspaceAccess={fullWorkspaceAccess}
                        userRole={currentUser?.role}
                        serviceRequestAccess={currentUser?.serviceRequestAccess}
                        reportId={reportAccess?.reportId}
                        navigationDisabled={reportOnly}
                    />
                )}
                <main className={`min-w-0 flex-1 relative overflow-x-clip transition-all ${['organizationDashboard', 'gsocDashboard'].includes(currentPage as string) ? 'p-4 md:p-8' : 'container mx-auto p-4 md:p-8'}`}>
                    {msaAccessBlocked
                        ? <MsaAccessGate organization={currentOrganization!} currentUser={currentUser!} missing={msaMissing} />
                        : <>
                    {fullWorkspaceAccess && <Notepad />}
                    {!fullWorkspaceAccess && currentPage === 'organizationDashboard' && currentOrganization?.isTestOrganization && <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-950"><strong>Test Mode.</strong> Your organization can use the dashboard without a subscription. Service requests are unlimited and may be billed or completed for free.</div>}
                    {!fullWorkspaceAccess && currentPage === 'organizationDashboard' && !currentOrganization?.isTestOrganization && currentOrganization?.msaTrialMode === 'app' && currentOrganization.trialEndsAt && new Date(currentOrganization.trialEndsAt).getTime() > Date.now() && <FreeTrialNotice organization={currentOrganization} email={currentUser?.email || ''} canManage={!isClientView && canManageClientAccount(currentUser?.role)} reminderOnly />}
                    
                    {/* Strategic Intelligence Alert (ACAPS) */}
                    {newCrisisAlert && (
                        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[100] animate-in fade-in slide-in-from-top-4 duration-500">
                            <div className="bg-slate-900/95 backdrop-blur-xl border border-amber-500/30 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-5 min-w-[380px] max-w-[500px]">
                                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center text-amber-500 shrink-0 animate-pulse">
                                    <span className="text-2xl font-bold">🆘</span>
                                </div>
                                <div className="flex-grow">
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-500 mb-1">New Strategic Intelligence Alert</p>
                                    <h4 className="text-sm font-bold leading-tight line-clamp-1">{newCrisisAlert.title}</h4>
                                    <p className="text-xs text-slate-400 mt-1 font-medium italic">Detected in {newCrisisAlert.country}</p>
                                </div>
                                <button 
                                    onClick={() => {
                                        setNewCrisisAlert(null);
                                        handleNavigate({ page: 'organizationDashboard' });
                                    }}
                                    className="bg-amber-500 hover:bg-amber-600 text-slate-900 p-2 rounded-xl transition-all shadow-lg shadow-amber-500/20 group"
                                >
                                    <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 7l5 5m0 0l-5 5m5-5H6"/></svg>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Unified Policy Handling */}
                    {['terms-of-service', 'privacy-policy', 'data-and-ai-policy', 'legal-disclaimer', 'subscription-agreement', 'methodology'].includes(currentPage as string) ? (
                        currentPage === 'methodology' ? (
                            <MethodologyLibrary onOpenGlossary={handleOpenGlossary} />
                        ) : (
                            <LegalPage 
                                doc={currentPage as LegalDoc} 
                                onNavigateHome={handleLegalBack}
                                backLabel={legalBackLabel}
                            />
                        )
                    ) : (
                        <>
                            {currentPage === 'resiliencyHub' && (
                                <ResiliencyHub 
                                    currentUser={currentUser ?? null}
                                    currentOrganization={currentOrganization ?? null}
                                    onBack={() => handleNavigate({ page: 'organizationDashboard' })}
                                    onNavigate={handleNavigate}
                                    riskData={riskData}
                                    navParams={navParams}
                                    onRequestHelp={handleRequestHelp}
                                    onOpenChatBot={() => handleNavigate({ page: 'help' })}
                                    onNavigateToGtgDashboard={() => handleNavigate({ page: 'gtg_dashboard' })}
                                    onNavigateToGsocDashboard={() => handleNavigate({ page: 'gsocDashboard' })}
                                    onNavigateToSuite={(suiteName) => handleNavigate({ page: 'suiteDashboard', params: { suiteName } })}
                                    onOpenAvToolsModal={() => setIsAvToolsOpen(true)}
                                    students={students}
                                    onUpdateSrmProjectModule={handleUpdateSrmProjectModule}
                                    activeFramework={activeFramework}
                                />
                            )}
                            {currentPage === 'global_risk_register' && currentOrganization && (
                                <GlobalMasterRiskRegister 
                                    organization={currentOrganization}
                                    onNavigateToProject={(projectId) => handleNavigate({ page: 'srm_command_center', params: { projectId } })}
                                    readOnly={!fullWorkspaceAccess}
                                />
                            )}
                            {currentPage === 'reportLibrary' && currentOrganization && (
                                <IntelligenceLibraryPage organization={currentOrganization} onOpenReport={reportId => handleNavigate({ page: 'analysisDisplay', params: { reportId } })} />
                            )}
                            {currentPage === 'consultantReport' && fullWorkspaceAccess && currentOrganization && navParams.reportId &&
                                <ConsultantReportWorkspace key={navParams.reportId} organizationId={currentOrganization.id} draftId={navParams.reportId} />}
                            {currentPage === 'deliveredReport' && navParams.reportId &&
                                <ConsultantReportView key={navParams.reportId} reportId={navParams.reportId} inline onClose={() => handleNavigate({ page: 'reportLibrary' })} />}
                            {currentPage === 'serviceRequests' && currentOrganization && currentUser && <ServiceRequestsPage organization={currentOrganization} currentUser={currentUser} initialRequestId={navParams.serviceRequestId} initialServiceCode={navParams.serviceCode} initialTemplateDraftId={navParams.serviceRequestDraftId} initialFrameworkRequestId={navParams.frameworkRequestId} onOpenReport={reportId => handleNavigate({ page: 'analysisDisplay', params: { reportId } })} onOpenDashboard={projectId => handleNavigate({ page: 'srm_command_center', params: { projectId, view: 'dashboard' } })} onOpenDailyMicro={country => { void (async () => { const iso3 = (COUNTRY_CODES as Record<string, { iso3: string }>)[country]?.iso3; if (!iso3) return handleNavigate({ page: 'organizationDashboard' }); try { const snapshot = await getDoc(doc(db, 'daily_country_analysis', iso3)); if (snapshot.data()?.status === 'success') return handleNavigate({ page: 'CountryAnalysisResults', params: { reportData: snapshot.data(), country, isDailyMicro: true } }); } catch { /* Dashboard shows report status. */ } handleNavigate({ page: 'organizationDashboard' }); })(); }} />}
                            {currentPage === 'myAccount' && currentOrganization && currentUser && ['owner', 'admin'].includes(currentUser.role) && <MyAccountPage organization={currentOrganization} currentUser={currentUser} users={orgUsers} onViewBilling={() => handleNavigate({ page: 'subscriptionManagement' })} />}
                            {currentPage === 'subscriptionManagement' && currentOrganization && canManageClientAccount(currentUser?.role) && <BillingInvoicesPage organization={currentOrganization} />}
                            {currentPage === 'help' && currentOrganization && currentUser && <HelpGuidancePage organization={currentOrganization} currentUser={currentUser} industry={currentIndustry || 'Global'} helpContext={helpContext} onNavigate={handleNavigate} onOpenHelpdesk={(category) => { setHelpdeskCategory(category); setIsHelpdeskModalOpen(true); }} ticketRefreshKey={helpdeskTicketRefresh} showMyTickets={!isOrganizationAccess} fullWorkspaceAccess={fullWorkspaceAccess} />}
                            {/* HARD GATE: Only show dashboard if active. Otherwise show setup. */}
        {currentPage === 'organizationDashboard' && (
            (() => {
                const isActive = fullWorkspaceAccess || hasMsaAccess(currentOrganization);

                if (isActive) {
                    if (fullWorkspaceAccess && currentOrganization?.tier === 'agency') {
                        return (
                            <AgencyDashboard
                                currentOrganization={currentOrganization}
                                currentUser={currentUser!}
                                onNavigate={handleNavigate}
                                onActivateClient={handleActivateClient}
                            />
                        );
                    }

                    return navParams.iso3 ? (
                        <CountryDashboard
                            organization={currentOrganization!}
                            currentUser={currentUser!}
                            iso3={navParams.iso3}
                            riskData={riskData}
                            onNavigate={handleNavigate}
                            readOnly={!fullWorkspaceAccess}
                        />
                    ) : (
                        <OrganizationDashboard 
                            organization={currentOrganization!} 
                            currentUser={currentUser!} 
                            onNavigate={handleNavigate} 
                            riskData={riskData}
                            alerts={globalAcapsData as any[]}
                            onRequestHelp={handleRequestHelp}
                            readOnly={!fullWorkspaceAccess}
                        />
                    );
                } else if (checkoutPending) {
                    // ✅ Post-payment holding screen — auto-exits when webhook fires or after 45s
                    return (
                        <div className="h-[60vh] flex flex-col items-center justify-center bg-slate-900/50 backdrop-blur-sm rounded-3xl border border-blue-500/20 text-white p-12">
                            <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mb-8"></div>
                            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">Finalizing Your Intelligence Suite</h2>
                            <p className="text-slate-400 mt-4 text-center max-w-md">We've received your payment. Securely provisioning your organization's resources and activating your tactical dashboard...</p>
                            <div className="mt-8 flex gap-2">
                                <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                                <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                                <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></span>
                            </div>
                            <button 
                                onClick={() => { setCheckoutPending(false); window.history.replaceState({}, '', `${window.location.pathname}${window.location.hash}`); }}
                                className="mt-8 text-xs text-slate-500 hover:text-slate-300 underline transition-colors"
                            >
                                Taking too long? Click here to continue →
                            </button>
                        </div>
                    );
                } else if (currentOrganization?.subscriptionStatus === 'inactive') {
                    return (
                        <InactiveAccountView 
                            organization={currentOrganization!} 
                            currentUser={currentUser!} 
                            onLogout={logOut}
                        />
                    );
                } else if (currentOrganization && currentUser) {
                    return (
                        <SubscriptionSetup 
                            orgId={currentOrganization.id} 
                            currentUser={currentUser} 
                            onSubscriptionSuccess={handleSubscriptionSuccess}
                            onLogout={logOut}
                            preselectedPlan={pendingPlan || 'individual'}
                            onNavigate={(doc) => handleNavigate({ page: doc })}
                        />
                    );
                } else {
                    return (
                        <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                            <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
                        </div>
                    );
                }
            })()
        )}
                            {currentPage === 'knowledgeHub' && <KnowledgeHub onNavigate={handleNavigate} onLogout={logOut} customIndustries={customIndustries} currentUser={currentUser ?? null} />}
                            {currentPage === 'pillarsDashboard' && (
                                <PillarsDashboard 
                                    onNavigate={handleNavigate}
                                    purpose={navParams.purpose}
                                    clientName={navParams.clientName}
                                />
                            )}
                            {currentPage === 'instructionManual' && <InstructionManual onBack={() => handleNavigate({ page: previousPage, params: previousNavParams })} />}
                    {currentPage === 'suiteDashboard' && navParams.suiteName && (
                        <SuiteDashboard 
                            key={navParams.suiteName}
                            suiteName={navParams.suiteName === 'HSSE Compliance' ? 'Company Business Resiliency and Crises Management Program' : navParams.suiteName}
                            navParams={navParams} 
                            onBack={() => {
                                // M1 FIX: Preserve suite context on Back. Return to wherever
                                // the user came from (previousPage) instead of always resetting
                                // to organizationDashboard. SRM project context is still
                                // the most specific override and takes priority.
                                if (navParams.projectId || navParams.srmProject) {
                                    handleNavigate({ page: 'srm_command_center', params: { projectId: navParams.projectId } });
                                } else if (previousPage && (previousPage as string) !== 'suiteDashboard' && previousPage !== currentPage) {
                                    handleNavigate({ page: previousPage, params: previousNavParams });
                                } else {
                                    handleNavigate({ page: 'organizationDashboard' });
                                }
                            }}
                            onRequestHelp={handleRequestHelp} 
                            onOpenChatBot={() => handleNavigate({ page: 'help' })}
                            onNavigate={handleNavigate} 
                            students={students} 
                            onCreateCustomCourse={enrollLearner}
                            onEnrollStudent={enrollLearner}
                            onUpdateStudentCourse={updateLearnerCourse}
                            onOpenAvToolsModal={() => setIsAvToolsOpen(true)} 
                            onNavigateToGsocDashboard={() => handleNavigate({ page: 'gsocDashboard' })} 
                            onNavigateToGtgDashboard={() => handleNavigate({ page: 'gtg_dashboard' })} 
                            onNavigateToSuite={(suiteName) => handleNavigate({ page: 'suiteDashboard', params: { suiteName } })} 
                            onOpenGlossary={handleOpenGlossary}
                            currentUser={currentUser ?? null} 
                            currentOrganization={currentOrganization ?? null} 
                            riskData={riskData} 
                            alerts={globalAcapsData}
                            onUpdateSrmProjectModule={handleUpdateSrmProjectModule}
                            activeFramework={activeFramework}
                        />
                    )}
                    {currentPage === 'gsocDashboard' && (<SuiteDashboard 
                            suiteName="Threat Analysis Hub" 
                            navParams={navParams} 
                            onBack={() => handleNavigate({ page: 'organizationDashboard' })} 
                            onRequestHelp={handleRequestHelp} 
                            onOpenChatBot={() => handleNavigate({ page: 'help' })}
                            onNavigate={handleNavigate} 
                            students={students} 
                            onOpenAvToolsModal={() => setIsAvToolsOpen(true)} 
                            onNavigateToGsocDashboard={() => handleNavigate({ page: 'gsocDashboard' })} 
                            onNavigateToGtgDashboard={() => handleNavigate({ page: 'gtg_dashboard' })} 
                            onNavigateToSuite={(suiteName) => handleNavigate({ page: 'suiteDashboard', params: { suiteName } })} 
                            onOpenGlossary={handleOpenGlossary}
                            currentUser={currentUser ?? null} 
                            currentOrganization={currentOrganization ?? null} 
                            riskData={riskData} 
                            alerts={globalAcapsData}
                        />)}
                    {currentPage === 'srm_command_center' && (
                        <SrmCommandCenter 
                            organization={currentOrganization ?? null} 
                            onNavigate={handleNavigate} 
                            projectId={navParams.projectId}
                            initialView={navParams.view as any}
                            activeClientId={activeClient?.id}
                            activeClientName={activeClient?.name}
                            onDeploy={handleDeployFramework}
                            navParams={navParams}
                            readOnly={!fullWorkspaceAccess}
                        />
                    )}
                    {currentPage === 'srmFrameworkMonitor' && (
                        <SrmCommandCenter 
                            organization={currentOrganization ?? null} 
                            onNavigate={handleNavigate} 
                            projectId={navParams.framework?.projectId || navParams.frameworkId}
                            initialView={'frameworks' as any}
                            onDeploy={handleDeployFramework}
                            navParams={navParams}
                            readOnly={!fullWorkspaceAccess}
                        />
                    )}
                    {currentPage === 'industryHub' && <IndustrySelection onSelectIndustry={(ind) => handleNavigate({ page: 'dashboard', params: { industry: ind } })} customIndustries={customIndustries} onDeleteIndustry={()=>{}} onBack={() => handleNavigate({ page: 'organizationDashboard' })} onNavigate={(p) => handleNavigate({ page: p })} />}
                    {currentPage === 'customIndustryBuilder' && <CustomIndustryBuilder onSave={()=>{}} onBack={() => handleNavigate({ page: 'industryHub' })} customIndustries={customIndustries} onDelete={()=>{}} onNavigate={handleNavigate} />}
                    {currentPage === 'dashboard' && navParams.industry && <Dashboard db={db} isLoaded={isLoaded} industry={navParams.industry} onNavigateToGtgDashboard={() => handleNavigate({ page: 'gtg_dashboard' })} onNavigateToGsocDashboard={() => handleNavigate({ page: 'gsocDashboard' })} onRequestHelp={handleRequestHelp} customIndustries={customIndustries} onNavigateToSuite={(suiteName) => handleNavigate({ page: 'suiteDashboard', params: { suiteName } })} onOpenAvToolsModal={() => setIsAvToolsOpen(true)} onNavigate={handleNavigate} />}
                    {currentPage === 'gtg_dashboard' && <GlobalThreatGroupDashboard onBack={() => handleNavigate({ page: 'organizationDashboard' })} onNavigate={handleNavigate} organization={currentOrganization} params={navParams} />}
                    {currentPage === 'agencyDashboard' && currentOrganization && currentUser && (
                        <AgencyDashboard
                            currentOrganization={currentOrganization!}
                            currentUser={currentUser!}
                            onNavigate={handleNavigate}
                            onActivateClient={handleActivateClient}
                        />
                    )}
                    {currentPage === 'clientDetail' && navParams.clientId && currentOrganization && currentUser && (
                        <ClientDetailPage
                            clientId={navParams.clientId as string}
                            currentOrganization={currentOrganization!}
                            currentUser={currentUser!}
                            onNavigate={handleNavigate}
                            onActivateClient={handleActivateClient}
                            onBack={() => handleNavigate({ page: 'agencyDashboard' })}
                        />
                    )}
                    {currentPage === 'training' && <TrainingPage onNavigateHome={() => handleNavigate({ page: 'organizationDashboard' })} />}
                    {currentPage === 'contact' && (
                        <Contact 
                            onNavigate={handleNavigate} 
                            currentUser={currentUser ?? null}
                            currentOrganization={currentOrganization ?? null}
                        />
                    )}
                    {(currentPage === 'CountryAnalysisResults' || currentPage === 'analysisDisplay' || currentPage === 'OSINTDossierResults') && (
                        isRefreshingAnalysis ? (
                            <div className="flex items-center justify-center min-h-[60vh]">
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                                    <p className="text-slate-500 animate-pulse">Generating fresh OSINT analysis...</p>
                                </div>
                            </div>
                        ) : (
                            <AnalysisResultDisplay 
                                reportData={navParams?.reportData || navParams?.result} 
                                result={navParams?.result || navParams?.reportData} 
                                country={navParams?.country || navParams?.iso3} 
                                alerts={navParams?.alerts ?? (navParams?.isDailyMicro === true ? globalAcapsData : undefined)}
                                onNavigate={handleNavigate}
                                srmProject={navParams.srmProject}
                                srmModule={navParams.srmModule}
                                onSaveToFramework={handleUpdateSrmProjectModule}
                                isDailyMicro={navParams?.isDailyMicro === true ? true : undefined}
                                activeWorkflow={navParams?.activeWorkflowId as string | undefined}
                                reportId={navParams?.reportId}
                                reportDate={navParams?.reportDate}
                                clientId={navParams?.clientId as string | undefined}
                                organizationId={currentOrganization?.id}
                                readOnly={!fullWorkspaceAccess}
                                onRegenerate={async context => {
                                    const displayedReport = navParams?.reportData || navParams?.result;
                                    const isEnterpriseAudit = (displayedReport?.reportType || context?.reportType) === 'EnterpriseRiskAudit';
                                    const isS02Revision = context?.deliverySnapshot && context.reportType === 'FacilityProtectionResult' && context.serviceRequestId;
                                    const country = navParams?.country || context?.country;
                                    const reportArea = displayedReport?.reportArea || context?.reportArea;
                                    if (!isEnterpriseAudit && !isS02Revision && !country && !reportArea) {
                                        void showAppAlert('This report cannot be regenerated without its original context.');
                                        return;
                                    }
                                    
                                    setIsRefreshingAnalysis(true);
                                    try {
                                        const functions = getFunctions(undefined, 'us-central1');
                                        if (isS02Revision) {
                                            if (!currentOrganization?.id)
                                                throw new Error('Organization context is unavailable.');
                                            const revise = httpsCallable(functions, 'manageS02FacilityPlan', { timeout: 540000 });
                                            await revise({ action: 'regenerateDelivered', organizationId: currentOrganization.id,
                                                requestId: context.serviceRequestId });
                                            handleNavigate({ page: 'suiteDashboard', params: { suiteName: 'Security Risk Management',
                                                activeWorkflowId: 's02_facility_plan', serviceRequestId: context.serviceRequestId } });
                                            return;
                                        }
                                        if (reportArea) {
                                            const refreshArea = httpsCallable(functions, 'saveDailyReportArea', { timeout: 120000 });
                                            const response = await refreshArea({ area: reportArea, save: false });
                                            const report = (response.data as any).report;
                                            if (!report)
                                                throw new Error('The regional report is unavailable.');
                                            setNavParams(prev => ({ ...prev, result: report, reportData: report, isDailyMicro: true }));
                                            return;
                                        }
                                        if (isEnterpriseAudit) {
                                            if (!currentOrganization?.id)
                                                throw new Error('Organization context is unavailable.');
                                            const assets = organizationGlobalAssets(currentOrganization);
                                            const threatLevelContext = globalAcapsData.filter(alert => {
                                                const severity = String((alert as any).severity || (alert as any).crisis_severity || '').toLowerCase();
                                                return severity === 'critical' || severity === 'high';
                                            }).length;
                                            const generateAudit = httpsCallable(functions, 'generateEnterpriseRiskAudit');
                                            const refreshResult = await generateAudit({
                                                organizationId: currentOrganization.id,
                                                industry: currentOrganization.industry || 'General',
                                                assets,
                                                threatLevelContext
                                            });
                                            const regeneratedReport = refreshResult.data as any;
                                            let reportId = navParams?.reportId;
                                            if (db && currentUser) {
                                                try {
                                                    const { sanitizeFirestoreData } = await import('./components/reports/ReportShared');
                                                    const reportRef = await addDoc(collection(db, 'generated_reports'), {
                                                        reportType: 'EnterpriseRiskAudit',
                                                        type: 'enterprise_audit',
                                                        workflowName: 'Enterprise Risk Audit',
                                                        suiteName: 'Enterprise Command Center',
                                                        title: `Enterprise Risk Audit — ${getOrganizationDisplayName(currentOrganization)} — ${formatLocalDate(new Date())}`,
                                                        organizationId: currentOrganization.id,
                                                        resultData: sanitizeFirestoreData(regeneratedReport),
                                                        timestamp: serverTimestamp(),
                                                        userEmail: currentUser.email,
                                                        userName: currentUser.displayName || currentUser.email
                                                    });
                                                    reportId = reportRef.id;
                                                } catch (persistError) {
                                                    console.warn('Regenerated enterprise audit could not be archived:', persistError);
                                                }
                                            }
                                            setNavParams(prev => ({
                                                ...prev,
                                                result: regeneratedReport,
                                                reportData: regeneratedReport,
                                                reportId
                                            }));
                                        } else {
                                            const generateMacro = httpsCallable(functions, 'onDemandAnalysisProxy', { timeout: 540000 });
                                            const refreshResult = await generateMacro({
                                                analysisType: 'Comprehensive Country Geopolitical & Risk Analysis',
                                                forceRefresh: true,
                                                persistDailyCountry: navParams?.isDailyMicro === true,
                                                formData: { country, iso3: country }
                                            });
                                            if (refreshResult.data) {
                                                const regeneratedReport = (refreshResult.data as any).result;
                                                setNavParams(prev => ({
                                                    ...prev,
                                                    result: regeneratedReport,
                                                    reportData: regeneratedReport
                                                }));
                                            }
                                        }
                                    } catch (error: any) {
                                        console.error("Analysis Regeneration Error:", error);
                                        void showAppAlert(`Failed to regenerate analysis: ${error?.message || 'Unknown error'}`);
                                    } finally {
                                        setIsRefreshingAnalysis(false);
                                    }
                                }}
                            />
                        )
                    )}
                    {currentPage === 'GovernanceStructure' && (
                        <GovernanceStructureDisplay
                            data={navParams?.reportData || navParams?.result}
                            country={navParams?.country}
                            onNavigate={handleNavigate}
                            onBack={() => handleNavigate({ page: 'CountryAnalysisResults', params: {
                                ...navParams,
                                _govBack: true,
                            }})}
                        />
                    )}
                    {currentPage === 'pending_clearance' && (
                        <PendingClearancePage
                            userEmail={authUser?.email || undefined}
                            onSignOut={logOut}
                        />
                    )}
                    {currentPage === 'countryIntel' && navParams.countryId && currentOrganization && (
                        <CountryIntelPage
                            iso3={navParams.countryId as string}
                            organizationId={currentOrganization.id}
                            onNavigate={handleNavigate}
                            onBack={() => handleNavigate({ page: 'gsocDashboard' })}
                        />
                    )}
                    {/* TEMP — Remove after Aug 22, 2026 */}
                    {currentPage === 'devSprint' && currentUser?.adminRole === 'Super' && (
                        <DevImplementationDashboard />
                    )}
                    </>
                )}
                        </>}
                </main>
            </div>
            <Footer onNavigate={(doc) => handleNavigate({ page: doc })} />
            {isAvToolsOpen && <AudioVisualToolsModal onClose={() => setIsAvToolsOpen(false)} />}
            {isAssetManagerOpen && (
                <AssetManagerModal 
                    onClose={() => setIsAssetManagerOpen(false)} 
                    activeClientId={navParams.clientId} 
                />
            )}
            <HelpdeskModal 
                isOpen={isHelpdeskModalOpen} 
                onClose={() => setIsHelpdeskModalOpen(false)} 
                category={helpdeskCategory}
                user={currentUser || null}
                organization={currentOrganization || null}
                onSubmitted={() => setHelpdeskTicketRefresh(previous => previous + 1)}
            />
            <GlossaryDrawer 
                isOpen={isGlossaryOpen}
                initialSearch={glossarySearch}
                onClose={() => {
                    setIsGlossaryOpen(false);
                    setGlossarySearch(null);
                }}
            />
            </div>
        </GlobalAssetProvider>
        </ReportNavigationContext.Provider>
    );
};

export default App;
